import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import PropertyDetailsSection from '../Properties/AddProperty/PropertyDetailsSection';
import CustomPriceSection from '../Properties/AddProperty/CustomPriceSection';
import StandardQuantityPopup from '../Properties/AddProperty/StandardQuantityPopup';
import '../Properties/AddProperty/addProperty.css';
import './pendingClientRequests.css';

// Option lists mirror the admin AddProperty form.
const cleanerOptions = [1, 2, 3, 4].map((n) => ({ value: n, label: `${n} Cleaner${n > 1 ? 's' : ''}` }));
const bedCountOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: `${n} Bed${n === 1 ? '' : 's'}` }));
const bedroomOptions = [
    { value: 0, label: 'Studio' },
    ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: `${n} Bedroom${n > 1 ? 's' : ''}` })),
];
const bathroomOptions = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8]
    .map((n) => ({ value: n, label: `${n} Bathroom${n > 1 ? 's' : ''}` }));

// Full editable "Add Property"-style review of a client property request.
// Renders the same fields/sub-components as the admin AddProperty form,
// pre-filled from the request. Approve creates the property from the admin's
// final values; Reject captures a reason.
export default function PropertyRequestReview({ request, onClose, onDone, readOnlyOutcome = null }) {
    const p = request.payload || {};
    const setupPhotos = Array.isArray(p.setup_photos) ? p.setup_photos : [];

    const [propertyData, setPropertyData] = useState({
        address_line_1: p.address_line_1 || '',
        unit_number: p.unit_number || '',
        area: p.area || '',
        google_maps_link: p.google_maps_link || '',
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
        google_place_id: p.google_place_id ?? null,
        google_place_desc: p.google_place_desc || '',
        bedrooms: p.bedrooms ?? '',
        bathrooms: p.bathrooms ?? '',
        single_beds: p.single_beds ?? 0,
        double_beds: p.double_beds ?? 1,
        super_king_beds: p.super_king_beds ?? 0,
        required_cleaners: p.required_cleaners ?? 1,
        is_linen_hire: !!p.is_linen_hire,
        is_beach_towel: !!p.is_beach_towel,
        is_dolce_gusto: !!p.is_dolce_gusto,
        is_nespresso: !!p.is_nespresso,
        is_custom_price: false,
        custom_price: '',
        is_custom_sq: false,
        standard_quantities: [],
        permanent_notes: p.notes || '',
        warehouse_note: '',
        scheduling_note: '',
        is_id_required: !!p.is_id_required,
        is_company_id_required: !!p.is_company_id_required,
        is_accompany_id_required: !!p.is_accompany_id_required,
        is_permit_needed: !!p.is_permit_needed,
        property_access: p.property_access || '',
        wifi_name: p.wifi_name || '',
        wifi_password: p.wifi_password || '',
    });

    // Multi-billing: the payload carries billing_profile_id but not the entity
    // label — resolve it from the client's billing profiles (same pattern as
    // the property GeneralDetails "Billed To" row).
    const [billedToLabel, setBilledToLabel] = useState(null);
    useEffect(() => {
        setBilledToLabel(null);
        if (!p.billing_profile_id || !request.client_id) return undefined;
        let cancelled = false;
        axios.get(`/api/clients/${request.client_id}/billing-profiles`)
            .then((res) => {
                if (cancelled) return;
                const match = (res.data?.profiles || []).find(
                    (prof) => prof.id === p.billing_profile_id
                );
                setBilledToLabel(match ? (match.label || match.legal_name) : null);
            })
            .catch(() => { /* fall back to the raw id below */ });
        return () => { cancelled = true; };
    }, [p.billing_profile_id, request.client_id]);

    const [showSQ, setShowSQ] = useState(false);
    const [editable, setEditable] = useState(false); // read-only until "Make Changes"
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const rejectReasonRef = useRef(null);

    // When entering reject mode, bring the reason box (at the bottom) into view
    // and focus it, so it's obvious what to do next.
    useEffect(() => {
        if (rejectMode && rejectReasonRef.current) {
            rejectReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current.focus();
        }
    }, [rejectMode]);

    const setField = (patch) => setPropertyData((prev) => ({ ...prev, ...patch }));

    const handleBedroomChange = (opt) => {
        const bedrooms = opt.value;
        let doubleBeds = 1;
        let cleaners = 1;
        if (bedrooms === 2) doubleBeds = 2;
        else if (bedrooms >= 3) { doubleBeds = bedrooms; cleaners = 2; }
        setField({ bedrooms, double_beds: doubleBeds, required_cleaners: cleaners });
    };

    const handleEditCustomSQ = () => {
        if (propertyData.bedrooms === '' || propertyData.bedrooms === null || propertyData.bedrooms === undefined) {
            alert('Please select Bedrooms first.');
            return;
        }
        setShowSQ(true);
    };

    const approve = async () => {
        if (propertyData.bedrooms === '' || propertyData.bathrooms === '') {
            setError('Bedrooms and bathrooms are required.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(`/api/client-change-requests/${request.id}/approve`, {
                admin_fields: {
                    ...propertyData,
                    notes: propertyData.permanent_notes,
                    custom_price: propertyData.is_custom_price && propertyData.custom_price !== ''
                        ? parseFloat(propertyData.custom_price) : null,
                    standard_quantities: propertyData.is_custom_sq ? propertyData.standard_quantities : [],
                },
            });
            // Idempotent path: another admin already approved this request, so
            // NONE of the edits made here were applied. Warn rather than show a
            // false "approved" banner.
            if (/already approved/i.test(res.data?.message || '')) {
                window.alert('This request was already approved by someone else — your changes here were NOT applied. Refreshing the list.');
            }
            onDone('approved', res.data?.message, res.data?.result_id);
        } catch (err) {
            console.error('Error approving property request:', err);
            setError(err.response?.data?.error || err.response?.data?.message || 'Failed to approve request.');
        } finally {
            setSubmitting(false);
        }
    };

    // Don't let the modal close mid-submit — a failure after unmount would be
    // silent, and the admin would never see the error or recovery instructions.
    const safeClose = () => { if (!submitting) onClose(); };

    const reject = async () => {
        if (!rejectReason.trim()) {
            setError('A rejection reason is required.');
            rejectReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current?.focus();
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`/api/client-change-requests/${request.id}/reject`, { reason: rejectReason.trim() });
            onDone('rejected');
        } catch (err) {
            console.error('Error rejecting property request:', err);
            setError(err.response?.data?.error || 'Failed to reject request.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pcr-modal-overlay" onClick={safeClose}>
            <div className="pcr-modal pcr-modal--form" onClick={(e) => e.stopPropagation()}>
                <div className="pcr-modal-header">
                    <div>
                        <h3>Review New Property</h3>
                        <div className="pcr-modal-sub">
                            {request.client_display_name || request.client_company_name} · requested by {request.requested_by_first_name} {request.requested_by_last_name}
                        </div>
                    </div>
                    <button className="pcr-modal-close" onClick={safeClose} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pcr-modal-body">
                    {/* Multi-billing: which entity the client picked for this
                        property. Absent for the default entity / non-multi
                        clients — nothing renders and nothing changes. */}
                    {p.billing_profile_id && (
                        <div className="pcr-hint" style={{ marginBottom: 12 }}>
                            <strong>Billed to:</strong>{' '}
                            {billedToLabel || `Billing entity #${p.billing_profile_id}`}
                            {' — approving assigns the property to this billing entity, so its invoices go there instead of the default entity.'}
                        </div>
                    )}

                    {setupPhotos.length > 0 && (
                        <div className="pcr-section">
                            <div className="pcr-section-title">Apartment setup photos ({setupPhotos.length})</div>
                            <div className="pcr-photos">
                                {setupPhotos.map((photo, i) => (
                                    <a className="pcr-photo" key={photo.file_key || i} href={photo.file_url} target="_blank" rel="noopener noreferrer">
                                        <img src={photo.file_url} alt={`Setup ${i + 1}`} loading="lazy" />
                                        {photo.description && <div className="pcr-photo-desc">{photo.description}</div>}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {!editable && (
                        <div className="pcr-readonly-note">
                            Read-only preview of the client&apos;s request. Click <strong>Make Changes</strong> to edit any field before approving.
                        </div>
                    )}

                    {/* The real admin property form, pre-filled — locked until "Make Changes" */}
                    <div className={`new-property-form pcr-embedded-form pcr-review-form ${editable ? '' : 'pcr-review-form--locked'}`}>
                        <PropertyDetailsSection newProperty={propertyData} setNewProperty={setPropertyData} />

                        <div className="form-section">
                            <h3>Bedroom Configuration</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Bedrooms</label>
                                    <Select options={bedroomOptions} value={bedroomOptions.find((o) => o.value === propertyData.bedrooms) || null} onChange={handleBedroomChange} placeholder="Select" />
                                </div>
                                <div className="form-group">
                                    <label>Bathrooms</label>
                                    <Select options={bathroomOptions} value={bathroomOptions.find((o) => o.value === propertyData.bathrooms) || null} onChange={(opt) => setField({ bathrooms: opt.value })} placeholder="Select" />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Single Beds</label>
                                    <Select options={bedCountOptions} value={bedCountOptions.find((o) => o.value === propertyData.single_beds)} onChange={(opt) => setField({ single_beds: opt.value })} placeholder="Single beds" />
                                </div>
                                <div className="form-group">
                                    <label>Double Beds</label>
                                    <Select options={bedCountOptions} value={bedCountOptions.find((o) => o.value === propertyData.double_beds)} onChange={(opt) => setField({ double_beds: opt.value })} placeholder="Double beds" />
                                </div>
                                <div className="form-group">
                                    <label>Super King Beds</label>
                                    <Select options={bedCountOptions} value={bedCountOptions.find((o) => o.value === (propertyData.super_king_beds || 0)) || bedCountOptions[0]} onChange={(opt) => setField({ super_king_beds: opt.value })} placeholder="Super King beds" />
                                </div>
                            </div>
                        </div>

                        <div className="form-section property-services-section">
                            <h3>Service, Pricing &amp; Amenities</h3>
                            <div className="form-row property-services-row">
                                <div className="form-group property-select-group">
                                    <label className="property-toggle-label">Standard Staffing</label>
                                    <Select options={cleanerOptions} value={cleanerOptions.find((o) => o.value === propertyData.required_cleaners)} onChange={(opt) => setField({ required_cleaners: opt.value })} placeholder="Select" />
                                </div>

                                <div className="form-group property-toggle-group">
                                    <label className="property-toggle-label">Linen Hire</label>
                                    <div className="property-toggle-inline">
                                        <label className="switch">
                                            <input type="checkbox" checked={propertyData.is_linen_hire}
                                                onChange={(e) => { const checked = e.target.checked; setField({ is_linen_hire: checked, is_beach_towel: checked ? propertyData.is_beach_towel : false }); }} />
                                            <span className="slider"></span>
                                        </label>
                                    </div>
                                </div>

                                {propertyData.is_linen_hire && (
                                    <div className="form-group property-toggle-group">
                                        <label className="property-toggle-label">LH Beach Towel</label>
                                        <div className="property-toggle-inline">
                                            <label className="switch">
                                                <input type="checkbox" checked={propertyData.is_beach_towel} onChange={(e) => setField({ is_beach_towel: e.target.checked })} />
                                                <span className="slider"></span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="form-group property-toggle-group">
                                    <label className="property-toggle-label">Custom Laundry Quantity</label>
                                    <div className="property-toggle-inline">
                                        <label className="switch">
                                            <input type="checkbox" checked={!!propertyData.is_custom_sq}
                                                onChange={(e) => { const checked = e.target.checked; setField({ is_custom_sq: checked, standard_quantities: checked ? propertyData.standard_quantities : [] }); }} />
                                            <span className="slider"></span>
                                        </label>
                                        {propertyData.is_custom_sq && (
                                            <span className="edit-custom-sq-link" onClick={handleEditCustomSQ}>Edit Custom Quantity</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-row property-services-row">
                                <div className="form-group property-amenities-group">
                                    <label className="property-toggle-label">Coffee Capsules Amenities</label>
                                    <div className="property-amenities-inline">
                                        <div className="property-toggle-group small">
                                            <span className="property-toggle-label-sm">Nescafe Dolce Gusto</span>
                                            <label className="switch">
                                                <input type="checkbox" checked={!!propertyData.is_dolce_gusto} onChange={(e) => setField({ is_dolce_gusto: e.target.checked })} />
                                                <span className="slider"></span>
                                            </label>
                                        </div>
                                        <div className="property-toggle-group small">
                                            <span className="property-toggle-label-sm">Nespresso</span>
                                            <label className="switch">
                                                <input type="checkbox" checked={!!propertyData.is_nespresso} onChange={(e) => setField({ is_nespresso: e.target.checked })} />
                                                <span className="slider"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <CustomPriceSection propertyData={propertyData} setPropertyData={setPropertyData} />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Permanent Notes</label>
                            <textarea value={propertyData.permanent_notes} onChange={(e) => setField({ permanent_notes: e.target.value })} placeholder="Notes for cleaners on each visit at this property." />
                        </div>
                        <div className="form-group">
                            <label>Warehouse Note</label>
                            <textarea value={propertyData.warehouse_note} onChange={(e) => setField({ warehouse_note: e.target.value })} placeholder="Permanent notes for warehouse team." />
                        </div>
                        <div className="form-group">
                            <label>Scheduling Note</label>
                            <textarea value={propertyData.scheduling_note} onChange={(e) => setField({ scheduling_note: e.target.value })} placeholder="Internal scheduling remarks." />
                        </div>

                        <div className="form-row scheduling-toggles-row">
                            {[
                                ['ID Required', 'is_id_required'],
                                ['Company ID Required', 'is_company_id_required'],
                                ['Accompany ID Required', 'is_accompany_id_required'],
                                ['Permit Needed', 'is_permit_needed'],
                            ].map(([label, key]) => (
                                <div className="form-group" key={key}>
                                    <label className="property-toggle-label">{label}</label>
                                    <label className="switch">
                                        <input type="checkbox" checked={!!propertyData[key]} onChange={(e) => setField({ [key]: e.target.checked })} />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            ))}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Property Access Info</label>
                                <input type="text" value={propertyData.property_access} onChange={(e) => setField({ property_access: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Wi-Fi Name</label>
                                <input type="text" value={propertyData.wifi_name} onChange={(e) => setField({ wifi_name: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Wi-Fi Password</label>
                                <input type="text" value={propertyData.wifi_password} onChange={(e) => setField({ wifi_password: e.target.value })} />
                            </div>
                        </div>
                    </div>

                    {rejectMode && (
                        <div className="pcr-section pcr-reason-block pcr-reason-block--reject">
                            <div className="pcr-section-title">Rejection reason (visible to client)</div>
                            <textarea ref={rejectReasonRef} className="pcr-reject-reason" value={rejectReason} maxLength={500} placeholder="Explain why this request is being rejected…" onChange={(e) => setRejectReason(e.target.value)} />
                        </div>
                    )}

                    {!!error && <div className="pcr-error">{error}</div>}
                </div>

                {/* Read-only history view (2026-08-06): rejected/
                    cancelled/approved rows open for inspection — outcome
                    instead of action buttons. */}
                {readOnlyOutcome ? (
                    <div className="pcr-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{readOnlyOutcome.statusLabel}</strong>
                            {readOnlyOutcome.reviewerName ? <> by <strong>{readOnlyOutcome.reviewerName}</strong></> : null}
                            {readOnlyOutcome.reviewedAt ? <> · {readOnlyOutcome.reviewedAt}</> : null}
                            {readOnlyOutcome.reason ? <div><strong>Reason:</strong> {readOnlyOutcome.reason}</div> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {readOnlyOutcome.onOpenEntity ? (
                                <button className="pcr-btn pcr-btn--primary" onClick={readOnlyOutcome.onOpenEntity}>
                                    {readOnlyOutcome.entityLabel}
                                </button>
                            ) : null}
                            <button className="pcr-btn pcr-btn--ghost" onClick={onClose}>Close</button>
                        </div>
                    </div>
                ) : (
                <div className="pcr-modal-actions pcr-modal-actions--split">
                    <div className="pcr-actions-left">
                        {!rejectMode && !editable && (
                            <button className="pcr-btn pcr-btn--ghost" onClick={() => setEditable(true)} disabled={submitting}>
                                Make Changes
                            </button>
                        )}
                    </div>
                    <div className="pcr-actions-right">
                        {rejectMode ? (
                            <>
                                <button className="pcr-btn pcr-btn--ghost" onClick={() => { setRejectMode(false); setError(''); }} disabled={submitting}>Back</button>
                                <button className="pcr-btn pcr-btn--danger" onClick={reject} disabled={submitting}>{submitting ? 'Rejecting…' : 'Confirm Reject'}</button>
                            </>
                        ) : (
                            <>
                                <button className="pcr-btn pcr-btn--ghost pcr-btn--danger-text" onClick={() => { setRejectMode(true); setError(''); }} disabled={submitting}>Reject</button>
                                <button className="pcr-btn pcr-btn--primary" onClick={approve} disabled={submitting}>{submitting ? 'Approving…' : 'Approve & Create Property'}</button>
                            </>
                        )}
                    </div>
                </div>
                )}

                {showSQ && (
                    <StandardQuantityPopup
                        propertyId={null}
                        initialQuantities={propertyData.standard_quantities}
                        bedrooms={propertyData.bedrooms}
                        singleBeds={propertyData.single_beds}
                        doubleBeds={propertyData.double_beds}
                        superKingBeds={propertyData.super_king_beds}
                        isBeachTowel={propertyData.is_beach_towel}
                        onSave={(quantities) => setPropertyData((prev) => ({ ...prev, is_custom_sq: true, standard_quantities: quantities }))}
                        onClose={() => setShowSQ(false)}
                    />
                )}
            </div>
        </div>
    );
}
